import React, { useState } from 'react';
import { UploadState, formatBytes } from '@adac-mp/shared';
import { useViewport } from '../../hooks/useViewport';
import {
	Typography,
	Button,
	CameraIcon,
	UploadFileIcon,
	DocumentIcon,
	DeleteIcon,
	DocumentSuccessIcon,
} from '../../index';

interface IFileUploadProps {
	state: UploadState;
	currentFile?: File;
	deleteDisabled: boolean;
	handleChange: (fileList: FileList) => void;
	handleDelete: () => void;
}

export const FileUpload = ({ state, currentFile, deleteDisabled, handleChange, handleDelete }: IFileUploadProps) => {
	const inputRef = React.useRef<HTMLInputElement | null>(null);
	const { viewport } = useViewport();
	const [dragActive, setDragActive] = useState(false);
	const [deleteHover, setDeleteHover] = useState(false);

	// handle drag events
	const handleDrag = function (e) {
		e.preventDefault();
		e.stopPropagation();
		if (e.type === 'dragenter' || e.type === 'dragover') {
			setDragActive(true);
		} else if (e.type === 'dragleave') {
			setDragActive(false);
		}
	};

	const handleDrop = function (e) {
		e.preventDefault();
		e.stopPropagation();
		setDragActive(false);
		if (e.dataTransfer?.files && e.dataTransfer.files[0]) {
			handleChange(e.dataTransfer.files);
		}
	};

	return (
		<>
			{state === 'default' ? (
				<form
					className="w-full relative flex justify-center"
					data-cy="file-upload-form"
					onDragEnter={(e) => handleDrag(e)}
					onSubmit={(e) => e.preventDefault()}>
					<input
						ref={inputRef}
						data-cy="file-upload-input"
						id="input-file-upload"
						type="file"
						accept="image/jpeg"
						className="hidden"
						onChange={(event) => {
							const input = event.target as HTMLInputElement;
							if (!input.files?.length) {
								return;
							}

							handleChange(input.files);
						}}
					/>
					<label
						className="py-6 px-4 flex justify-center items-center flex-col rounded bg-white border border-gray-500 border-dashed w-full cursor-pointer transition-all duration-500 hover:bg-gray-700 hover:border-gray-400 md:hover:scale-105"
						htmlFor="input-file-upload"
						tabIndex={0}
						onKeyDown={(e) => {
							if (e.key === 'Enter' || e.key === ' ') {
								inputRef.current?.click();
							}
						}}>
						{viewport === 'desktop' ? (
							<>
								<span className="bg-gray-600 p-3 rounded-full mb-2">
									<UploadFileIcon fill="#3d3d3d" />
								</span>
								<Typography
									variant="title"
									size="lg"
									color="title"
									className="text-center mb-3"
									component="h5">
									Klicken Sie hier oder <br /> ziehen Sie Ihre Datei direkt in diesen Bereich.
								</Typography>
							</>
						) : (
							<>
								<span className="bg-gray-600 p-2 rounded-full mb-2">
									<CameraIcon size="48" />
								</span>
								<Typography
									variant="title"
									size="lg"
									color="title"
									className="text-center mb-3"
									component="h5">
									Klicken Sie hier, um ein Foto von Ihrem Fahrzeugschein hochzuladen
								</Typography>
							</>
						)}

						<Typography className="text-center" component="p" color="lightText">
							Als JPG mit mind. 200 KB und max. 10 MB Größe.
						</Typography>
					</label>
					{dragActive && (
						<div
							className="absolute w-full h-full inset-0"
							onDragEnter={(e) => handleDrag(e)}
							onDragLeave={(e) => handleDrag(e)}
							onDragOver={(e) => handleDrag(e)}
							onDrop={(e) => handleDrop(e)}
						/>
					)}
				</form>
			) : (
				<div
					className="relative w-full rounded flex justify-between self-center bg-white px-4 py-3"
					data-cy="file-uploaded">
					<div className="flex items-center">
						{state === 'loading' ? <DocumentIcon /> : <DocumentSuccessIcon />}
						<div className="ml-4">
							{currentFile && (
								<Typography size="xs" className="inline-block bg-gray-600 rounded px-1 mb-1.5">
									{currentFile.type} - {formatBytes(currentFile.size, 1)}
								</Typography>
							)}

							<Typography variant="title" size="lg" component="p" color="title" className="mb-1">
								Fahrzeugschein
							</Typography>
							<Typography color="lightText" component="p">
								{state === 'loading' ? 'Wird hochgeladen' : 'Erfolgreich hinzugefügt'}
							</Typography>
						</div>
					</div>
					{state === 'success' && (
						<Button
							disabled={deleteDisabled}
							variant="tertiary"
							className="mb-0 bg-white"
							data-cy="file-delete-button"
							onClick={handleDelete}
							onMouseOver={() => setDeleteHover(true)}
							onMouseOut={() => setDeleteHover(false)}>
							<DeleteIcon fill={deleteHover ? '#121212' : '#3D3D3D'} />
						</Button>
					)}
					{state === 'loading' && (
						<div className="absolute bottom-0 left-0 h-1 w-full overflow-hidden">
							<div className="h-full bg-primary animate-loader w-2/5" />
						</div>
					)}
				</div>
			)}
		</>
	);
};
